<template>
  <b-card class="mt-3">
    <h4><translate>Shipment Documents and Images</translate>
      <b-button variant="secondary" size="sm" v-b-modal.modalAttachFile v-if="allowCreate">
        <font-awesome-icon icon="plus-circle" />
        <translate>Attach File</translate>
      </b-button>
      <b-modal id="modalAttachFile" ref="modalAttachFile" size="lg" hide-footer @hidden="resetAttachment">
        <template #modal-title><translate>Attach File</translate></template>
        <template #modal-header-close><img src="@/assets/img/ic_close.svg" alt="×" /></template>
        <div class="bulk-import"
            v-if="!attachment.file">
          <b-form-file
            v-model="attachment.file"
            :placeholder="this.$gettext('Click to select a file or drop it here...')"
            :drop-placeholder="this.$gettext('Drop file here...')"
            @input="attachFileChanged"
            ref="importedAttachments"
            id="importedAttachments"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.PDF,.JPG,.JPEG,.PNG,.WEBP,.GIF"
            class="my-3"
          ></b-form-file>
        </div>
        <div v-else>
          <b-card no-body class="p-2">
            <b-media vertical-align="center" class="align-items-center">
              <template #aside>
                <div v-if="imageUrl" style="width: 100px; max-height: 100px;" class="d-flex justify-content-center align-items-center">
                  <img :src="imageUrl" alt="" style="max-height: 100px;" class="img-thumbnail" />
                </div>
                <div v-else style="width: 100px; height: 100px;" class="d-flex justify-content-center align-items-center img-thumbnail">
                  <font-awesome-icon icon="file-pdf" style="font-size: 55px; vertical-align: middle;" class="text-danger"/>
                </div>
              </template>
              <h6 class="mb-0">{{attachment.file.name}}</h6>
              <div v-translate="{fileSize: humanFileSize(attachment.file.size, true)}">
                Size: <strong>%{fileSize}</strong>
              </div>
              <div v-translate="{type: attachment.file.type}">
                Type: <strong>%{type}</strong>
              </div>
            </b-media>
          </b-card>
          <b-row>
            <b-col>
              <b-form-group>
                <label for="attachment-comment" v-translate>Comment</label>
                <b-form-textarea id="attachment-comment" v-model="attachment.comment" />
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group>
                <label for="attachment-type" v-translate>Type <small class="text-danger">(required)</small></label>
                <b-form-select id="attachment-type" v-model="attachment.type" :options="attachmentTypes" required>
                  <template #first>
                    <option :value="null" v-translate>Select the Type</option>
                  </template>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-form-group class="col-12">
              <b-button size="sm" @click.prevent="uploadAttachment" :disabled="busy">
                <translate>Upload</translate>
              </b-button>
            </b-form-group>
          </b-row>
        </div>
      </b-modal>
    </h4>
    <b-row v-if="shipping.photos?.length > 0">
      <b-col cols="12" :md="cols" v-for="(attachment, index) in shipping.photos" :key="index" v-if="attachment.type !== 'SIGNATURE'">
        <a href="#" @click.prevent="$parent.currentPicture = !attachmentIsPdf(attachment.full_path) ? attachment.full_path : attachment">
          <b-card class="mb-2 p-2" no-body>
            <span v-if="allowDelete" class="bg-danger text-white d-block text-center" style="cursor: pointer; width: 1rem; height: 1rem; line-height: 1rem; position: absolute; top: 0; right: 0; border-radius: 0 3px 0 0;" href="#" @click.prevent.stop="deleteAttachment(attachment)">×</span>
            <b-media vertical-align="center" class="align-items-center">
              <template #aside>
                <div style="width: 60px; max-height: 60px;" class="d-flex justify-content-center align-items-center" v-if="!attachmentIsPdf(attachment.full_path)">
                  <img :src="attachment.full_path" alt="" style="max-height: 60px;" class="img-thumbnail" />
                </div>
                <div style="width: 60px; height: 60px;" class="d-flex justify-content-center align-items-center img-thumbnail" v-else>
                  <font-awesome-icon icon="file-pdf" style="font-size: 30px; vertical-align: middle;" class="text-danger"/>
                </div>
              </template>
              <h6 class="mb-0">{{statusStrings[attachment.type] || attachment.type}} <b-badge variant="dark" v-if="attachment.source === 'web'">web</b-badge></h6>
              <div class="text-muted" v-translate="{userName: attachment.applied_by?.name || 'NN', createdAt: moment.utc(attachment.meta?.created_at_offline || attachment.created_at).tz($tz).format('L LT')}">
                By <strong>%{userName}</strong> on <strong>%{createdAt}</strong>
              </div>
              <div class="text-success" v-if="withOcrStatus && attachment.content_status === 'reviewed'" v-translate="{userName: attachment.meta.meta.applied_by?.name || 'NN', updatedAt: moment.utc(attachment.updated_at).tz($tz).format('L LT')}">
                Reviewed by <strong>%{userName}</strong> on <strong>%{updatedAt}</strong>
              </div>
              <div class="text-warning" v-else-if="withOcrStatus && attachment.content_status === 'manual'">
                <span v-translate>Review Status: <strong>OCR need review</strong></span>
                <b-link @click.stop :to="{name: 'OcrControl', query: {qf: JSON.stringify({
                    'shipping-files-review-status': 'manual',
                    'shipping-files-shipping-id': shipping.id,
                    'shipping-files-created-at': {
                      startDate: null,
                      endDate: null,
                      dateRangeActive: false
                    }
                  })}}" target="_blank"
                  v-if="ecosystem.product !== 'carriers'"
                >
                  <b-badge>
                    <font-awesome-icon icon="edit" />
                    <translate>Edit</translate>
                  </b-badge>
                </b-link>
              </div>
              <div class="text-success" v-else-if="withOcrStatus && attachment.content_status === 'automatic'" v-translate>
                Review Status: <strong>Automatic OCR OK</strong>
              </div>
              <div v-if="attachment.comment" v-html="$options.filters.brize(attachment.comment)"></div>
            </b-media>
          </b-card>
        </a>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
  ShippingsService
} from '@/common/api.service'
import constants from '@/common/constants'

export default {
  name: 'ShippingAttachments',
  props: {
    shipping: {
      type: Object,
      default: () => {},
      required: true
    },
    allowCreate: {
      type: Boolean,
      default: false
    },
    allowDelete: {
      type: Boolean,
      default: false
    },
    cols: {
      type: [String, Number],
      default: 12
    },
    withOcrStatus: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      file: null,
      imageUrl: null,
      attachment: {
        file: null,
        full_path: null,
        comment: null,
        type: null,
        source: 'web'
      },
      attachmentTypes: [
        'PICKEDUP',
        'DELIVERED',
        'INCIDENCE',
        'SIGNATURE',
        'PICKUPSIGNATURE'
      ],
      busy: false
    }
  },
  mixins: [constants],
  mounted () {
    this.attachment.type = this.attachmentTypes.indexOf(this.shipping.status) !== -1 ? this.shipping.status : null
  },
  methods: {
    attachFileChanged(e) {
      if (e.type.indexOf('image') !== -1) {
        this.imageUrl = URL.createObjectURL(e)
      }
    },
    resetAttachment() {
      this.imageUrl = null
      Object.assign(this.$data.attachment, this.$options.data.apply(this).attachment)
    },
    uploadAttachment() {
      this.busy = true
      ShippingsService.attachFile(this.shipping.id, this.attachment)
        .then(() => {
          this.busy = false
          this.$refs.modalAttachFile.hide()
          this.emitUpdate()
        })
    },
    deleteAttachment(attachment) {
      if (confirm(this.$gettext('Are you sure you want to DELETE this file?'))) {
        ShippingsService.deleteAttachment(this.shipping.id, attachment.id)
          .then(() => {
            this.$refs.modalAttachFile.hide()
            this.emitUpdate()
          })
      }
    },
    attachmentIsPdf(url) {
      return (url = url.substr(1 + url.lastIndexOf('/')).split('?')[0].split('#')[0]).substr(url.lastIndexOf('.')) === '.pdf'
    },
    emitUpdate() {
      this.$emit('updated', true)
    }
  }
}
</script>
